import React, { useRef } from 'react';
import type videojs from 'video.js';
import { detectVideoMimeType } from '@common/media';
import { cn } from '../../../lib/utils';
import ProtectedMediaWrapper from '../../media/ProtectedMediaWrapper';
import VideoPlayer from './VideoJsPlayer';
import useImageViewerStore from '../../../store/ImageViewerStore';
import { useMessageImages } from '../../../contexts/MessageImagesContext';

interface TMessageAttachment {
  url: string;
  label?: string;
  /** True when rendered as one tile of a multi-media grid. */
  inGrid?: boolean;
}

const VIDEO_URL = import.meta.env.VITE_VIDEO_URL;

function VideoMessage({ url, label, inGrid }: TMessageAttachment) {
  const playerRef = useRef<ReturnType<typeof videojs> | null>(null);
  const { setIsViewerOpen, setCurrentImageUrl, setCurrentImageAlt, setImages, setCurrentIndex } =
    useImageViewerStore();
  const { images } = useMessageImages();

  // Mirrors ImageMessage.showImagePreview so a grid video reaches the same
  // fullscreen viewer at the same index.
  const showVideoPreview = () => {
    if (!url) return;
    setImages(images);
    const index = images.findIndex((img) => img.url === url);
    setCurrentIndex(index >= 0 ? index : 0);
    setCurrentImageUrl(url);
    setCurrentImageAlt(label || 'video preview');
    setIsViewerOpen(true);
  };

  const videoJsOptions: IVideoOptionsProps = {
    autoplay: false,
    controls: true,
    responsive: true,
    playsinline: true,
    fluid: true,
    disablePictureInPicture: true,
    controlsList: 'nodownload',
    sources: [
      {
        src: `${url}${url?.includes(VIDEO_URL) ? '#t=0.001' : ''}`,
        type: detectVideoMimeType(url),
      },
    ],
  };

  const handlePlayerReady = (player: ReturnType<typeof videojs>) => {
    playerRef.current = player;

    player.on('waiting', () => {
      // videojs.log('player is waiting');
    });

    player.on('dispose', () => {
      // videojs.log('player will dispose');
    });
  };

  // TELV1-565 — in a grid, render a static poster tile rather than a live
  // player. A ~170px video.js instance is unusable on a phone, iOS caps how
  // many video elements can decode concurrently, and a tile playing under the
  // viewer would sound over it. `#t=0.001` makes Safari paint the first frame
  // instead of a black box. Note this cannot be done by passing onClick to
  // ProtectedMediaWrapper below: it wires onClick on the overlay only, and the
  // player needs `noOverlay` to keep its controls reachable.
  if (inGrid) {
    // The seek fragment is appended on EVERY origin here, unlike the live
    // player above which only adds it for VIDEO_URL. That difference is
    // deliberate: the player has controls and a poster is cosmetic, but this
    // tile IS the poster — without the seek Safari paints a black box instead
    // of frame 0, whichever host the media is on. Guarded so a url that
    // already carries a fragment doesn't end up with two.
    const posterSrc = url?.includes('#') ? url : `${url}#t=0.001`;
    return (
      <ProtectedMediaWrapper onClick={showVideoPreview} className="relative w-full cursor-zoom-in">
        {/* No caption track: these are customer-supplied research stimuli and
            we have no caption files for them. An empty <track src=""> would
            satisfy the rule while giving a screen reader nothing. */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          src={posterSrc}
          preload="metadata"
          muted
          playsInline
          aria-label={label || 'video preview'}
          className="w-full h-auto rounded-lg bg-black"
          style={{ aspectRatio: '16 / 9', objectFit: 'contain' }}
        />
        <span
          aria-hidden
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
        >
          <span className="flex items-center justify-center w-14 h-14 rounded-full bg-black/55">
            <svg viewBox="0 0 24 24" className="w-7 h-7 translate-x-[2px]" fill="white">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </span>
      </ProtectedMediaWrapper>
    );
  }

  return (
    <ProtectedMediaWrapper noOverlay className={cn('flex items-center max-w-full w-[50rem]')}>
      <VideoPlayer options={videoJsOptions} onReady={handlePlayerReady} />
    </ProtectedMediaWrapper>
  );
}

export default React.memo(VideoMessage);
